import { useAuth } from 'hooks/useAuth';
import { isMasterRole } from 'util/access';

/**
 * Read/write state for one module, for use inside a page.
 *
 * The single place a stakeholder page asks "may I act here?", so the rule is
 * not restated (and drifted) in twenty components:
 *
 *     const { canRead, canWrite } = useModulePermission('mentoring_sessions');
 *
 * `canWrite` is the ONLY thing that should gate a create/update/delete/status
 * control. It is deliberately not "is this an admin" — a mentor with
 * `mentoring_sessions:write` gets the original session functionality, and one
 * with read alone sees the same sessions with the actions gone.
 *
 * Permission is a ceiling, never a widening: a page still applies its own
 * business rules underneath (a founder only ever acts on their own records, a
 * juror only on assigned programmes). Holding `write` does not bypass those.
 */
export function useModulePermission(resource) {
  const { role, can, hasResourceGrant } = useAuth();
  const canRead = can(resource, 'read') || hasResourceGrant?.(resource, 'read');
  // The platform master is provisioning-only across every module this hook
  // gates (Mentoring Sessions, Documents, Collaboration, Evaluations,
  // Funding, etc.) — it can view them but not write, matching the backend's
  // `forbid_master` on their mutating routes.
  const canWrite = (can(resource, 'write') || hasResourceGrant?.(resource, 'write')) && !isMasterRole(role);
  return { canRead, canWrite, readOnly: canRead && !canWrite };
}

export default useModulePermission;
